import { produce } from 'immer';
import { isEmpty } from 'lodash/fp';
import { extractError } from 'app/lib/error/errorMappings.js';
import {
    type GetJobActivityMetrics,
    type GetJobRunTimeline,
    JOB_DEFINITION_CREATED,
    JOB_ENVIRONMENT_CREATED,
    type JobActivityMetrics,
    type JobDefinitionEntry,
    type JobEnvironmentEntry,
    type JobRunEntry,
    type JobRunFilterOptions,
    JobRunStatus,
    type JobRunSummaryEntry,
    type JobRunTimelineEntry,
    type JobTriggerCandidatesByRole,
    type ListJobRuns,
    type SnowflakeFileFormats,
    type SnowflakeServiceAccountUserEntry,
    type SnowflakeStageFileInfo,
    type SnowflakeStages,
    type SnowflakeStoredProcedure,
    type StageDetails,
    type StageFiles,
    type JobRunAuditResult,
    type ModelTagCheckCandidate,
    type JobDefinitionDetailedEntry,
    type SnowflakeTableFqn,
    type LastJobRun
} from 'app/lib/types/server';
import { ErrorLevel, FetchStatus } from 'app/lib/constants';
import type { ExtractedError } from 'app/lib/error/types';
import { toStringFqn } from 'app/lib/utils/';
import type { TypedReduxActionOutcomes } from 'app/store/storeTypes';
import { COMPLETE_JOB_RUN_SUCCESS_JOB_RUN_COMPLETED } from 'app/store/__generated__/events/management';
import {
    CANCEL_JOB_RUN_ACTION_TYPE,
    CLEAR_SNOWFLAKE_STAGE_FILES_LIST_ACTION_TYPE,
    DELETE_JOB_DEFINITION_ACTION_TYPE,
    DELETE_JOB_ENVIRONMENT_ACTION_TYPE,
    DELETE_SNOWFLAKE_SERVICE_ACCOUNT_USER_ACTION_TYPE,
    GET_JOB_ACTIVITY_METRICS_ACTION_TYPE,
    GET_JOB_RUN_ACTION_TYPE,
    GET_LAST_JOB_RUN_ACTION_TYPE,
    GET_JOB_RUN_FILTER_OPTIONS_ACTION_TYPE,
    GET_JOB_RUN_TIMELINE_ACTION_TYPE,
    GET_PROJECTS_CONTAINING_TABLE_DEPLOYMENTS_ACTION_TYPE,
    GET_SNOWFLAKE_STAGE_DETAILS_ACTION_TYPE,
    GET_SNOWFLAKE_STAGE_FILE_INFO_ACTION_TYPE,
    GET_STORED_PROCEDURE_AUDIT_DATA_ACTION_TYPE,
    LIST_DATABASES_FOR_ENVIRONMENT_ACTION_TYPE,
    LIST_JOB_DEFINITIONS_ACTION_TYPE,
    LIST_JOB_ENVIRONMENTS_ACTION_TYPE,
    LIST_JOB_RUNS_ACTION_TYPE,
    LIST_SNOWFLAKE_FILE_FORMATS_ACTION_TYPE,
    LIST_SNOWFLAKE_SERVICE_ACCOUNT_USERS_ACTION_TYPE,
    LIST_SNOWFLAKE_STAGE_FILES_ACTION_TYPE,
    LIST_SNOWFLAKE_STAGES_ACTION_TYPE,
    LIST_STORED_PROCEDURE_CANDIDATES_ACTION_TYPE,
    PAUSE_JOB_TRIGGER_ACTION_TYPE,
    RESET_SET_ENVIRONMENT_ACTION_TYPE,
    RESET_SET_JOB_DEFINITION_ACTION_TYPE,
    RESET_SET_SERVICE_ACCOUNT_ACTION_TYPE,
    RESUME_JOB_TRIGGER_ACTION_TYPE,
    SET_JOB_DEFINITION_ACTION_TYPE,
    SET_JOB_ENVIRONMENT_ACTION_TYPE,
    SET_SNOWFLAKE_SERVICE_ACCOUNT_USER_ACTION_TYPE,
    TRIGGER_JOB_RUN_ACTION_TYPE,
    TRIGGER_JOB_RUNS_ACTION_TYPE,
    GET_MODEL_TAG_CHECK_CANDIDATES_ACTION_TYPE,
    CLEAR_MODEL_TAG_CHECK_CANDIDATES,
    GET_JOB_DEFINITION_ACTION_TYPE,
    GET_MODEL_TAG_SOURCE_TABLES_ACTION_TYPE,
    CLEAR_MODEL_TAG_SOURCE_TABLES,
    SET_JOB_TRIGGER_CANDIDATES_FILTER,
    GET_JOB_TRIGGER_CANDIDATES_BY_ROLE_ACTION_TYPE,
    SET_JOB_DEFINITION_FILTER
} from './types';
import type { Filter } from './constants';

type ActivityMetricsEntry = {
    isLoading: boolean;
    error: ExtractedError | null;
    metrics: JobActivityMetrics['entries'] | null;
    params: GetJobActivityMetrics | null;
};

type AuditDataEntry = {
    isLoading: boolean;
    error: ExtractedError | null;
    data: JobRunAuditResult | null;
};

type CandidateByRoleEntry = {
    entityId: string | null;
    isLoading: boolean;
    error: ExtractedError | null;
    options: JobTriggerCandidatesByRole | null;
    filter: Filter;
};

type ModelTagCheckCandidatesEntry = {
    isLoading: boolean;
    options: ModelTagCheckCandidate[] | null;
};

type ModelTagSourceTables = {
    isLoading: boolean;
    options: SnowflakeTableFqn[] | null;
};

type FiltersEntry = {
    isLoading: boolean;
    error: ExtractedError | null;
    options: JobRunFilterOptions | null;
};

type JobRun = {
    isLoading: boolean;
    error: ExtractedError | null;
    data: JobRunEntry | null;
};

type JobRunHistoryEntry = {
    isLoading: boolean;
    error: ExtractedError | null;
    data: JobRunSummaryEntry[] | null;
    totalCount: number;
};

type ServiceAccountEntry = {
    isLoading: boolean;
    error: ExtractedError | null;
    data: Record<string, SnowflakeServiceAccountUserEntry> | null;
};

type StoredProcedureCandidateEntry = {
    isLoading: boolean;
    error: ExtractedError | null;
    options: SnowflakeStoredProcedure[] | null;
};

type ProjectEntry = {
    uid: string;
    name: string;
};

export type JobsReduxSlice = {
    activityMetrics: ActivityMetricsEntry;
    auditData: AuditDataEntry;
    modelTagCheckCandidates: ModelTagCheckCandidatesEntry;
    modelTagSourceTables: ModelTagSourceTables;
    candidatesByRole: CandidateByRoleEntry;
    filters: FiltersEntry;
    projects: ProjectEntry[] | null;
    environmentsById: Record<string, JobEnvironmentEntry>;
    databasesByEnvironment: {
        status: FetchStatus;
        error: ExtractedError | null;
        environmentUid: string | null;
        entries: Array<string>;
    };
    jobDefinitionsById: Record<string, JobDefinitionEntry> | null;
    jobDefinitionDetails: {
        isLoadingById: Record<string, boolean>;
        error: ExtractedError | null;
        byId: Record<string, JobDefinitionDetailedEntry>;
    };
    jobRun: JobRun;
    lastJobRun: {
        isLoading: boolean;
        data: LastJobRun | null;
    };
    jobRunHistory: JobRunHistoryEntry;
    isLoadingJobDefinitions: boolean;
    isDeletingJobDefinition: boolean;
    isDeletingJobEnvironment: boolean;
    isDeletingServiceAccount: boolean;
    isSubmittingJobRun: boolean;
    isCancelingJobRun: boolean;
    serviceAccounts: ServiceAccountEntry;
    selectedJobDefinitionUid: string | null;
    selectedEnvironmentUid: string | null;
    selectedServiceAccountUid: string | null;
    setEnvironment: {
        status: FetchStatus;
        error: ExtractedError | null;
    };
    setJobDefinition: {
        status: FetchStatus;
        error: ExtractedError | null;
    };
    setServiceAccount: {
        status: FetchStatus;
        error: ExtractedError | null;
    };
    storedProcedureCandidatesList: StoredProcedureCandidateEntry;
    runningJobsByRunId: Record<string, string>;
    jobRunFilters: ListJobRuns | null;
    jobDefFilters: Filter;
    jobRunTimeline: {
        status: FetchStatus;
        error: ExtractedError | null;
        now: string | null;
        entries: Array<JobRunTimelineEntry>;
        params: GetJobRunTimeline | null;
    };
    createJobDefinitionDialog: {
        stagesList: {
            list: SnowflakeStages['value'] | null;
            isLoading: boolean;
            error: ExtractedError | null;
        };
        stagesDetails: {
            byStageFqn: Record<string, StageDetails>;
            isLoading: boolean;
            error: ExtractedError | null;
        };
        fileFormats: {
            list: SnowflakeFileFormats['fileFormats'] | null;
            isLoading: boolean;
            error: ExtractedError | null;
        };
        stageFiles: {
            list: StageFiles['files'] | null;
            isLoading: boolean;
            error: ExtractedError | null;
        };
        stageFileInfo: {
            info: SnowflakeStageFileInfo | null;
            isLoading: boolean;
            error: ExtractedError | null;
        };
    };
};

const CREATE_JOB_DEFINITION_DIALOG_INITIAL_STATE = {
    stagesList: {
        list: null,
        isLoading: false,
        error: null
    },
    stagesDetails: {
        byStageFqn: {},
        isLoading: false,
        error: null
    },
    fileFormats: {
        list: null,
        isLoading: false,
        error: null
    },
    stageFiles: {
        list: null,
        isLoading: false,
        error: null
    },
    stageFileInfo: {
        info: null,
        isLoading: false,
        error: null
    }
};

export const INITIAL_STATE: JobsReduxSlice = {
    activityMetrics: {
        isLoading: false,
        error: null,
        metrics: null,
        params: null
    },
    auditData: {
        isLoading: false,
        error: null,
        data: null
    },
    modelTagCheckCandidates: {
        isLoading: false,
        options: null
    },
    modelTagSourceTables: {
        isLoading: false,
        options: null
    },
    candidatesByRole: {
        entityId: null,
        isLoading: false,
        error: null,
        options: null,
        filter: {}
    },
    filters: {
        isLoading: false,
        error: null,
        options: null
    },
    projects: null,
    environmentsById: {},
    databasesByEnvironment: {
        status: FetchStatus.IDLE,
        error: null,
        environmentUid: null,
        entries: []
    },
    jobDefinitionsById: null,
    jobDefinitionDetails: {
        isLoadingById: {},
        error: null,
        byId: {}
    },
    jobRun: {
        isLoading: false,
        error: null,
        data: null
    },
    lastJobRun: {
        isLoading: false,
        data: null
    },
    jobRunHistory: {
        isLoading: false,
        error: null,
        data: null,
        totalCount: 0
    },
    isLoadingJobDefinitions: false,
    isDeletingJobDefinition: false,
    isDeletingJobEnvironment: false,
    isDeletingServiceAccount: false,
    isSubmittingJobRun: false,
    isCancelingJobRun: false,
    serviceAccounts: {
        isLoading: false,
        error: null,
        data: null
    },
    setEnvironment: {
        status: FetchStatus.IDLE,
        error: null
    },
    setJobDefinition: {
        status: FetchStatus.IDLE,
        error: null
    },
    setServiceAccount: {
        status: FetchStatus.IDLE,
        error: null
    },
    selectedJobDefinitionUid: null,
    selectedEnvironmentUid: null,
    selectedServiceAccountUid: null,
    storedProcedureCandidatesList: {
        isLoading: false,
        error: null,
        options: null
    },
    jobRunFilters: null,
    jobRunTimeline: {
        status: FetchStatus.IDLE,
        error: null,
        now: null,
        entries: [],
        params: null
    },
    createJobDefinitionDialog: CREATE_JOB_DEFINITION_DIALOG_INITIAL_STATE,
    runningJobsByRunId: {},
    jobDefFilters: {}
};

const updateScheduleRunInfo = (
    isEnabled: boolean,
    uid: string,
    { jobDefinitionDetails }: JobsReduxSlice
) => {
    const jobDefinitionTrigger = jobDefinitionDetails.byId?.[uid]?.scheduledTrigger;
    if (jobDefinitionTrigger) {
        jobDefinitionTrigger.enabled = isEnabled;
    }
};

export const jobsReducer = produce((draft, action: TypedReduxActionOutcomes) => {
    switch (action.type) {
        case GET_PROJECTS_CONTAINING_TABLE_DEPLOYMENTS_ACTION_TYPE.STARTED: {
            draft.projects = null;
            break;
        }
        case GET_PROJECTS_CONTAINING_TABLE_DEPLOYMENTS_ACTION_TYPE.SUCCEEDED: {
            const { entries } = action.payload;
            draft.projects = Object.entries(entries).map(([uid, name]) => ({
                uid,
                name
            }));
            break;
        }

        case GET_STORED_PROCEDURE_AUDIT_DATA_ACTION_TYPE.STARTED:
            draft.auditData.isLoading = true;
            draft.auditData.error = null;
            draft.auditData.data = null;
            break;
        case GET_STORED_PROCEDURE_AUDIT_DATA_ACTION_TYPE.SUCCEEDED:
            draft.auditData.data = action.payload;
            break;
        case GET_STORED_PROCEDURE_AUDIT_DATA_ACTION_TYPE.REJECTED:
        case GET_STORED_PROCEDURE_AUDIT_DATA_ACTION_TYPE.FAILED:
            draft.auditData.error = extractError(action.payload);
            break;
        case GET_STORED_PROCEDURE_AUDIT_DATA_ACTION_TYPE.ENDED:
            draft.auditData.isLoading = false;
            break;

        case GET_JOB_ACTIVITY_METRICS_ACTION_TYPE.STARTED:
            draft.activityMetrics.isLoading = true;
            draft.activityMetrics.error = null;
            draft.activityMetrics.metrics = null;
            draft.activityMetrics.params = action.payload;
            break;
        case GET_JOB_ACTIVITY_METRICS_ACTION_TYPE.SUCCEEDED:
            draft.activityMetrics.isLoading = false;
            draft.activityMetrics.metrics = action.payload.entries;
            break;
        case GET_JOB_ACTIVITY_METRICS_ACTION_TYPE.REJECTED:
        case GET_JOB_ACTIVITY_METRICS_ACTION_TYPE.FAILED:
            draft.activityMetrics.isLoading = false;
            draft.activityMetrics.error = extractError(action.payload);
            break;

        case LIST_JOB_ENVIRONMENTS_ACTION_TYPE.SUCCEEDED: {
            const { entries } = action.payload;
            draft.environmentsById = {};
            for (const entry of entries ?? []) {
                draft.environmentsById[entry.uid] = entry;
            }
            break;
        }

        case LIST_DATABASES_FOR_ENVIRONMENT_ACTION_TYPE.STARTED:
            draft.databasesByEnvironment.status = FetchStatus.FETCHING;
            draft.databasesByEnvironment.environmentUid = action.payload.environmentUid;
            draft.databasesByEnvironment.entries = [];
            break;
        case LIST_DATABASES_FOR_ENVIRONMENT_ACTION_TYPE.SUCCEEDED: {
            draft.databasesByEnvironment.status = FetchStatus.SUCCEEDED;
            draft.databasesByEnvironment.entries = action.payload.value.map((entry) => entry.name);
            break;
        }
        case LIST_DATABASES_FOR_ENVIRONMENT_ACTION_TYPE.REJECTED:
        case LIST_DATABASES_FOR_ENVIRONMENT_ACTION_TYPE.FAILED: {
            draft.databasesByEnvironment.status = FetchStatus.FAILED;
            draft.databasesByEnvironment.error = extractError(action.payload);
            break;
        }

        case GET_JOB_RUN_FILTER_OPTIONS_ACTION_TYPE.STARTED:
            draft.filters.isLoading = true;
            draft.filters.error = null;
            draft.filters.options = null;
            break;
        case GET_JOB_RUN_FILTER_OPTIONS_ACTION_TYPE.SUCCEEDED:
            draft.filters.isLoading = false;
            draft.filters.options = action.payload;
            break;
        case GET_JOB_RUN_FILTER_OPTIONS_ACTION_TYPE.REJECTED:
        case GET_JOB_RUN_FILTER_OPTIONS_ACTION_TYPE.FAILED:
            draft.filters.isLoading = false;
            draft.filters.error = extractError(action.payload);
            break;

        case LIST_STORED_PROCEDURE_CANDIDATES_ACTION_TYPE.STARTED:
            draft.storedProcedureCandidatesList.isLoading = true;
            draft.storedProcedureCandidatesList.error = null;
            draft.storedProcedureCandidatesList.options = null;
            break;
        case LIST_STORED_PROCEDURE_CANDIDATES_ACTION_TYPE.SUCCEEDED:
            draft.storedProcedureCandidatesList.options = action.payload.value ?? [];
            break;
        case LIST_STORED_PROCEDURE_CANDIDATES_ACTION_TYPE.REJECTED:
        case LIST_STORED_PROCEDURE_CANDIDATES_ACTION_TYPE.FAILED:
            draft.storedProcedureCandidatesList.error = extractError(action.payload);
            break;
        case LIST_STORED_PROCEDURE_CANDIDATES_ACTION_TYPE.ENDED:
            draft.storedProcedureCandidatesList.isLoading = false;
            break;
        case GET_JOB_TRIGGER_CANDIDATES_BY_ROLE_ACTION_TYPE.STARTED:
            draft.candidatesByRole.isLoading = true;
            draft.candidatesByRole.error = null;
            draft.candidatesByRole.options = null;
            break;
        case GET_JOB_TRIGGER_CANDIDATES_BY_ROLE_ACTION_TYPE.SUCCEEDED:
            draft.candidatesByRole.isLoading = false;
            draft.candidatesByRole.options = action.payload;
            break;
        case GET_JOB_TRIGGER_CANDIDATES_BY_ROLE_ACTION_TYPE.REJECTED:
        case GET_JOB_TRIGGER_CANDIDATES_BY_ROLE_ACTION_TYPE.FAILED:
            draft.candidatesByRole.isLoading = false;
            draft.candidatesByRole.error = extractError(action.payload);
            break;
        case SET_JOB_TRIGGER_CANDIDATES_FILTER: {
            if (isEmpty(action.payload)) {
                draft.candidatesByRole.filter = {};
                return;
            }
            const { type, value, name } = action.payload;
            if (value) {
                draft.candidatesByRole.filter[type] = { name, value };
            } else {
                delete draft.candidatesByRole.filter[type];
            }

            break;
        }
        case SET_JOB_DEFINITION_FILTER: {
            if (isEmpty(action.payload)) {
                draft.jobDefFilters = {};
                return;
            }
            const filters = action.payload;

            filters.forEach((filter) => {
                const { type, value, name } = filter;
                if (value) {
                    draft.jobDefFilters[type] = { name, value };
                } else {
                    delete draft.jobDefFilters[type];
                }
            });

            break;
        }
        case GET_MODEL_TAG_CHECK_CANDIDATES_ACTION_TYPE.STARTED:
            draft.modelTagCheckCandidates.isLoading = true;
            draft.modelTagCheckCandidates.options = null;
            break;
        case GET_MODEL_TAG_CHECK_CANDIDATES_ACTION_TYPE.SUCCEEDED:
            draft.modelTagCheckCandidates.options = action.payload.candidates;
            break;
        case GET_MODEL_TAG_CHECK_CANDIDATES_ACTION_TYPE.ENDED:
            draft.modelTagCheckCandidates.isLoading = false;
            break;
        case CLEAR_MODEL_TAG_CHECK_CANDIDATES:
            draft.modelTagCheckCandidates.options = null;
            break;

        case GET_MODEL_TAG_SOURCE_TABLES_ACTION_TYPE.STARTED:
            draft.modelTagSourceTables.isLoading = true;
            draft.modelTagSourceTables.options = null;
            break;
        case GET_MODEL_TAG_SOURCE_TABLES_ACTION_TYPE.SUCCEEDED:
            draft.modelTagSourceTables.options = action.payload.entries;
            break;
        case GET_MODEL_TAG_SOURCE_TABLES_ACTION_TYPE.ENDED:
            draft.modelTagSourceTables.isLoading = false;
            break;
        case CLEAR_MODEL_TAG_SOURCE_TABLES:
            draft.modelTagSourceTables.options = null;
            break;

        case LIST_JOB_DEFINITIONS_ACTION_TYPE.STARTED:
            draft.isLoadingJobDefinitions = true;
            draft.jobDefinitionsById = null;
            break;
        case LIST_JOB_DEFINITIONS_ACTION_TYPE.SUCCEEDED: {
            const { entries } = action.payload;
            draft.jobDefinitionsById = {};
            for (const entry of entries ?? []) {
                draft.jobDefinitionsById[entry.uid] = entry;
            }
            break;
        }
        case LIST_JOB_DEFINITIONS_ACTION_TYPE.ENDED:
            draft.isLoadingJobDefinitions = false;
            break;

        case GET_JOB_DEFINITION_ACTION_TYPE.STARTED:
            draft.jobDefinitionDetails.isLoadingById[action.meta.definitionUid] = true;
            break;
        case GET_JOB_DEFINITION_ACTION_TYPE.SUCCEEDED:
            draft.jobDefinitionDetails.byId[action.payload.uid] = action.payload;
            break;
        case GET_JOB_DEFINITION_ACTION_TYPE.REJECTED:
        case GET_JOB_DEFINITION_ACTION_TYPE.FAILED:
            draft.jobDefinitionDetails.error = extractError(action.payload);
            break;
        case GET_JOB_DEFINITION_ACTION_TYPE.ENDED:
            draft.jobDefinitionDetails.isLoadingById[action.meta.definitionUid] = false;
            break;
        case LIST_JOB_RUNS_ACTION_TYPE.STARTED:
            draft.jobRunHistory.isLoading = true;
            draft.jobRunHistory.error = null;
            if (action.payload.offset === 0) {
                draft.jobRunHistory.data = null;
            }
            draft.jobRunFilters = action.payload;
            break;

        case LIST_JOB_RUNS_ACTION_TYPE.SUCCEEDED: {
            const { entries, totalCount } = action.payload;
            if (draft.jobRunFilters?.offset === 0) {
                draft.jobRunHistory.data = entries;
            } else {
                draft.jobRunHistory.data = draft.jobRunHistory.data?.concat(entries) || entries;
            }
            draft.jobRunHistory.totalCount = totalCount;
            break;
        }
        case LIST_JOB_RUNS_ACTION_TYPE.REJECTED:
        case LIST_JOB_RUNS_ACTION_TYPE.FAILED:
            draft.jobRunHistory.error = extractError(action.payload);
            break;
        case LIST_JOB_RUNS_ACTION_TYPE.ENDED:
            draft.jobRunHistory.isLoading = false;
            break;

        case LIST_SNOWFLAKE_SERVICE_ACCOUNT_USERS_ACTION_TYPE.STARTED:
            draft.serviceAccounts.isLoading = true;
            draft.serviceAccounts.error = null;
            draft.serviceAccounts.data = null;
            break;

        case LIST_SNOWFLAKE_SERVICE_ACCOUNT_USERS_ACTION_TYPE.SUCCEEDED: {
            const { entries } = action.payload;
            draft.serviceAccounts.data = {};
            for (const entry of entries ?? []) {
                draft.serviceAccounts.data[entry.uid] = entry;
            }
            break;
        }
        case LIST_SNOWFLAKE_SERVICE_ACCOUNT_USERS_ACTION_TYPE.REJECTED:
        case LIST_SNOWFLAKE_SERVICE_ACCOUNT_USERS_ACTION_TYPE.FAILED:
            draft.serviceAccounts.error = extractError(action.payload);
            break;
        case LIST_SNOWFLAKE_SERVICE_ACCOUNT_USERS_ACTION_TYPE.ENDED:
            draft.serviceAccounts.isLoading = false;
            break;

        case GET_JOB_RUN_ACTION_TYPE.STARTED:
            draft.jobRun.isLoading = true;
            draft.jobRun.error = null;
            draft.jobRun.data = null;
            break;
        case GET_JOB_RUN_ACTION_TYPE.SUCCEEDED:
            draft.jobRun.data = action.payload;
            break;
        case GET_JOB_RUN_ACTION_TYPE.REJECTED:
        case GET_JOB_RUN_ACTION_TYPE.FAILED:
            draft.jobRun.error = extractError(action.payload);
            break;
        case GET_JOB_RUN_ACTION_TYPE.ENDED:
            draft.jobRun.isLoading = false;
            break;

        case GET_LAST_JOB_RUN_ACTION_TYPE.STARTED:
            draft.lastJobRun.isLoading = true;
            draft.lastJobRun.data = null;
            break;
        case GET_LAST_JOB_RUN_ACTION_TYPE.SUCCEEDED:
            draft.lastJobRun.data = action.payload;
            break;
        case GET_LAST_JOB_RUN_ACTION_TYPE.ENDED:
            draft.lastJobRun.isLoading = false;
            break;

        case CANCEL_JOB_RUN_ACTION_TYPE.SUCCEEDED:
            if (draft.jobRun?.data?.uid === action.meta.jobRunUid) {
                draft.jobRun.data.jobRunStatus = JobRunStatus.CANCELED;
            }
            break;

        case DELETE_JOB_DEFINITION_ACTION_TYPE.STARTED:
            draft.isDeletingJobDefinition = true;
            break;

        case DELETE_JOB_DEFINITION_ACTION_TYPE.SUCCEEDED: {
            const { uid } = action.params;
            if (draft.jobDefinitionsById !== null) {
                delete draft.jobDefinitionsById[uid];
            }
            draft.isDeletingJobDefinition = false;
            break;
        }

        case DELETE_JOB_DEFINITION_ACTION_TYPE.FAILED:
            draft.isDeletingJobDefinition = false;
            break;

        case DELETE_JOB_ENVIRONMENT_ACTION_TYPE.STARTED:
            draft.isDeletingJobEnvironment = true;
            break;
        case DELETE_JOB_ENVIRONMENT_ACTION_TYPE.SUCCEEDED: {
            const { uid } = action.params;
            delete draft.environmentsById[uid];
            break;
        }
        case DELETE_JOB_ENVIRONMENT_ACTION_TYPE.ENDED:
            draft.isDeletingJobEnvironment = false;
            break;

        case DELETE_SNOWFLAKE_SERVICE_ACCOUNT_USER_ACTION_TYPE.STARTED:
            draft.isDeletingServiceAccount = true;
            break;
        case DELETE_SNOWFLAKE_SERVICE_ACCOUNT_USER_ACTION_TYPE.SUCCEEDED: {
            const { uid } = action.params;
            if (draft.serviceAccounts.data) {
                delete draft.serviceAccounts.data[uid];
            }
            break;
        }
        case DELETE_SNOWFLAKE_SERVICE_ACCOUNT_USER_ACTION_TYPE.ENDED:
            draft.isDeletingServiceAccount = false;
            break;

        case SET_JOB_ENVIRONMENT_ACTION_TYPE.STARTED:
            draft.setEnvironment.status = FetchStatus.FETCHING;
            draft.setEnvironment.error = null;
            draft.selectedEnvironmentUid = null;
            break;
        case SET_JOB_ENVIRONMENT_ACTION_TYPE.SUCCEEDED:
            draft.setEnvironment.status = FetchStatus.SUCCEEDED;
            if (action.payload._kind === JOB_ENVIRONMENT_CREATED) {
                draft.selectedEnvironmentUid = action.payload.uid;
            }
            break;
        case SET_JOB_ENVIRONMENT_ACTION_TYPE.FAILED:
            draft.setEnvironment.status = FetchStatus.FAILED;
            draft.setEnvironment.error = extractError(action.payload);
            break;
        case RESET_SET_ENVIRONMENT_ACTION_TYPE:
            draft.setEnvironment.status = FetchStatus.IDLE;
            draft.setEnvironment.error = null;
            break;

        case SET_JOB_DEFINITION_ACTION_TYPE.STARTED:
            draft.setJobDefinition.status = FetchStatus.FETCHING;
            draft.setJobDefinition.error = null;
            draft.selectedJobDefinitionUid = null;
            break;
        case SET_JOB_DEFINITION_ACTION_TYPE.SUCCEEDED:
            if (action.payload._kind === JOB_DEFINITION_CREATED) {
                draft.selectedJobDefinitionUid = action.payload.uid;
            }
            draft.setJobDefinition.status = FetchStatus.SUCCEEDED;
            break;
        case SET_JOB_DEFINITION_ACTION_TYPE.FAILED:
        case SET_JOB_DEFINITION_ACTION_TYPE.REJECTED:
            draft.setJobDefinition.status = FetchStatus.FAILED;
            draft.setJobDefinition.error = extractError(action.payload);
            break;
        case RESET_SET_JOB_DEFINITION_ACTION_TYPE:
            draft.setJobDefinition.status = FetchStatus.IDLE;
            draft.setJobDefinition.error = null;
            draft.createJobDefinitionDialog = CREATE_JOB_DEFINITION_DIALOG_INITIAL_STATE;
            break;

        case SET_SNOWFLAKE_SERVICE_ACCOUNT_USER_ACTION_TYPE.STARTED:
            draft.setServiceAccount.status = FetchStatus.FETCHING;
            draft.setServiceAccount.error = null;
            draft.selectedServiceAccountUid = null;
            break;
        case SET_SNOWFLAKE_SERVICE_ACCOUNT_USER_ACTION_TYPE.SUCCEEDED:
            draft.setServiceAccount.status = FetchStatus.SUCCEEDED;
            draft.selectedServiceAccountUid = action.payload.uid;
            break;
        case SET_SNOWFLAKE_SERVICE_ACCOUNT_USER_ACTION_TYPE.FAILED:
            draft.setServiceAccount.status = FetchStatus.FAILED;
            draft.setServiceAccount.error = extractError(action.payload);
            break;
        case RESET_SET_SERVICE_ACCOUNT_ACTION_TYPE:
            draft.setServiceAccount.status = FetchStatus.IDLE;
            draft.setServiceAccount.error = null;
            break;

        case CANCEL_JOB_RUN_ACTION_TYPE.STARTED:
            draft.isCancelingJobRun = true;
            break;
        case CANCEL_JOB_RUN_ACTION_TYPE.ENDED:
            draft.isCancelingJobRun = false;
            break;

        case GET_JOB_RUN_TIMELINE_ACTION_TYPE.STARTED:
            draft.jobRunTimeline.status = FetchStatus.FETCHING;
            draft.jobRunTimeline.params = action.payload;
            break;
        case GET_JOB_RUN_TIMELINE_ACTION_TYPE.SUCCEEDED:
            draft.jobRunTimeline.status = FetchStatus.SUCCEEDED;
            draft.jobRunTimeline.now = new Date().toISOString();
            draft.jobRunTimeline.entries = action.payload.entries;
            break;
        case GET_JOB_RUN_TIMELINE_ACTION_TYPE.FAILED:
            draft.jobRunTimeline.status = FetchStatus.FAILED;
            draft.jobRunTimeline.error = extractError(action.payload);
            break;

        case PAUSE_JOB_TRIGGER_ACTION_TYPE.SUCCEEDED:
            updateScheduleRunInfo(false, action.params.uid, draft);
            break;

        case RESUME_JOB_TRIGGER_ACTION_TYPE.SUCCEEDED: {
            const { uid } = action.params;
            updateScheduleRunInfo(true, uid, draft);
            break;
        }

        case TRIGGER_JOB_RUN_ACTION_TYPE.STARTED:
            draft.isSubmittingJobRun = true;
            break;

        case TRIGGER_JOB_RUN_ACTION_TYPE.SUCCEEDED: {
            const { definitionUid, runUid } = action.payload;

            draft.runningJobsByRunId[runUid] = definitionUid;
            break;
        }

        case TRIGGER_JOB_RUN_ACTION_TYPE.ENDED:
            draft.isSubmittingJobRun = false;
            break;

        case TRIGGER_JOB_RUNS_ACTION_TYPE.STARTED:
            draft.isSubmittingJobRun = true;
            break;

        case TRIGGER_JOB_RUNS_ACTION_TYPE.SUCCEEDED: {
            const submitted = action.payload.submitted;
            for (const [definitionId, runUid] of Object.entries(submitted)) {
                draft.runningJobsByRunId[`_jrun${runUid}`] = `_jdef${definitionId}`;
            }
            break;
        }

        case TRIGGER_JOB_RUNS_ACTION_TYPE.ENDED:
            draft.isSubmittingJobRun = false;
            break;
        // @ts-expect-error incompatible type
        case COMPLETE_JOB_RUN_SUCCESS_JOB_RUN_COMPLETED: {
            // @ts-expect-error payload may not exist
            const jobRunUid = action?.payload?.runUid;
            if (jobRunUid && jobRunUid in draft.runningJobsByRunId) {
                delete draft.runningJobsByRunId[jobRunUid];
            }
            break;
        }

        case LIST_SNOWFLAKE_STAGES_ACTION_TYPE.STARTED:
            draft.createJobDefinitionDialog.stagesList.isLoading = true;
            draft.createJobDefinitionDialog.stagesList.error = null;
            break;
        case LIST_SNOWFLAKE_STAGES_ACTION_TYPE.SUCCEEDED:
            draft.createJobDefinitionDialog.stagesList.list = action.payload.value;
            break;
        case LIST_SNOWFLAKE_STAGES_ACTION_TYPE.FAILED:
            draft.createJobDefinitionDialog.stagesList.error = extractError(action.payload);
            break;
        case LIST_SNOWFLAKE_STAGES_ACTION_TYPE.ENDED:
            draft.createJobDefinitionDialog.stagesList.isLoading = false;
            break;

        case GET_SNOWFLAKE_STAGE_DETAILS_ACTION_TYPE.STARTED:
            draft.createJobDefinitionDialog.stagesDetails.isLoading = true;
            draft.createJobDefinitionDialog.stagesDetails.error = null;
            break;
        case GET_SNOWFLAKE_STAGE_DETAILS_ACTION_TYPE.SUCCEEDED:
            draft.createJobDefinitionDialog.stagesDetails.byStageFqn[
                toStringFqn(action.params.stage)
            ] = action.payload;
            break;
        case GET_SNOWFLAKE_STAGE_DETAILS_ACTION_TYPE.FAILED:
            draft.createJobDefinitionDialog.stagesDetails.error = extractError(action.payload);
            break;
        case GET_SNOWFLAKE_STAGE_DETAILS_ACTION_TYPE.ENDED:
            draft.createJobDefinitionDialog.stagesDetails.isLoading = false;
            break;

        case LIST_SNOWFLAKE_FILE_FORMATS_ACTION_TYPE.STARTED:
            draft.createJobDefinitionDialog.fileFormats.isLoading = true;
            draft.createJobDefinitionDialog.fileFormats.error = null;
            break;
        case LIST_SNOWFLAKE_FILE_FORMATS_ACTION_TYPE.SUCCEEDED:
            draft.createJobDefinitionDialog.fileFormats.list = action.payload.fileFormats;
            break;
        case LIST_SNOWFLAKE_FILE_FORMATS_ACTION_TYPE.FAILED:
            draft.createJobDefinitionDialog.fileFormats.error = extractError(action.payload);
            break;
        case LIST_SNOWFLAKE_FILE_FORMATS_ACTION_TYPE.ENDED:
            draft.createJobDefinitionDialog.fileFormats.isLoading = false;
            break;

        case LIST_SNOWFLAKE_STAGE_FILES_ACTION_TYPE.STARTED:
            draft.createJobDefinitionDialog.stageFiles.isLoading = true;
            draft.createJobDefinitionDialog.stageFiles.error = null;
            break;
        case LIST_SNOWFLAKE_STAGE_FILES_ACTION_TYPE.SUCCEEDED:
            draft.createJobDefinitionDialog.stageFiles.list = action.payload.files;
            break;
        case LIST_SNOWFLAKE_STAGE_FILES_ACTION_TYPE.FAILED:
            draft.createJobDefinitionDialog.stageFiles.error = extractError(
                action.payload,
                ErrorLevel.ERROR
            );
            break;
        case LIST_SNOWFLAKE_STAGE_FILES_ACTION_TYPE.ENDED:
            draft.createJobDefinitionDialog.stageFiles.isLoading = false;
            break;
        case CLEAR_SNOWFLAKE_STAGE_FILES_LIST_ACTION_TYPE:
            draft.createJobDefinitionDialog.stageFiles =
                CREATE_JOB_DEFINITION_DIALOG_INITIAL_STATE.stageFiles;
            break;

        case GET_SNOWFLAKE_STAGE_FILE_INFO_ACTION_TYPE.STARTED:
            draft.createJobDefinitionDialog.stageFileInfo.isLoading = true;
            draft.createJobDefinitionDialog.stageFileInfo.error = null;
            break;
        case GET_SNOWFLAKE_STAGE_FILE_INFO_ACTION_TYPE.SUCCEEDED:
            draft.createJobDefinitionDialog.stageFileInfo.info = action.payload;
            break;
        case GET_SNOWFLAKE_STAGE_FILE_INFO_ACTION_TYPE.FAILED:
            draft.createJobDefinitionDialog.stageFileInfo.error = extractError(action.payload);
            break;
        case GET_SNOWFLAKE_STAGE_FILE_INFO_ACTION_TYPE.ENDED:
            draft.createJobDefinitionDialog.stageFileInfo.isLoading = false;
            break;

        default:
            break;
    }
}, INITIAL_STATE);
